<template>
    <div class="general-props-component">
        <Loader v-if="loading" />
        <b-alert variant="danger" :show="loadError">
            {{$t('additional_load_error')}}
            <b-button size="sm" @click="loadDeviceProps($props.deviceId)">{{$t('retry')}}</b-button>
        </b-alert>
        <template v-if="!!source">
            <Protector v-if="saving" />
            <b-button
                variant="primary"
                :disabled="saving"
                @click="submitForm"
            >
                {{$t('monitoring.props.general.submit')}}
            </b-button>
            <b-button
                variant="secondary"
                :disabled="saving"
                @click="mapProps"
            >
                {{$t('monitoring.props.general.reset')}}
            </b-button>
            <b-alert
                class="mt-3"
                v-if="hasAnyAlert()"
                :variant="stateAlertVariant()"
                show
                dismissible
            >{{alertMessage}}</b-alert>
            <b-form @submit.prevent="submitForm">
                <b-form-group
                    label-cols-sm="4"
                    label-cols-md="3"
                    content-cols-sm
                    content-cols-md="7"
                    id="input-group-1"
                    :label="$t('monitoring.props.general.name')"
                    :description="$t('monitoring.props.general.name.description')"
                    label-for="deviceName"
                >
                    <b-form-input
                        id="deviceName"
                        :value="form.name"
                        v-model="form.name"
                        :disabled="saving"
                        type="text"
                        placeholder=""
                    />
                </b-form-group>

            </b-form>
        </template>
    </div>
</template>

<script lang="ts">
import Vue from "vue";
import Component from "vue-class-component";
import {Watch} from "vue-property-decorator";
import DeviceAdditionalDataRepository from "../../../Repository/DeviceAdditionalData/DeviceAdditionalDataRepository";
import {AdditionalDataDto} from "../../../Repository/DeviceAdditionalData/dto/AdditionalDataDto";
import Loader from "../../Common/Loader.vue";
import Protector from "../../Common/Protector.vue";

interface IForm {
    name?: string;
    imei?: string;
}

enum State {
    Empty,
    Success,
    Error
}

@Component({
    components: { Loader, Protector },
    props: {
        deviceId: { type: Number, required: true, },
    }
})
class GeneralProps extends Vue {
    form: IForm = {};
    source: AdditionalDataDto | null = null;
    state: State = State.Empty;
    alertMessage: string = '';
    saving: boolean = false;
    loading: boolean = false;
    loadError: boolean = false;
    disposed: boolean = false;
    loadRequestId: number = 0;
    saveRequestId: number = 0;

    beforeDestroy() {
        this.disposed = true;
        this.loadRequestId++;
        this.saveRequestId++;
    }

    mounted(): void {
        this.loadDeviceProps(this.$props.deviceId);
    }

    @Watch('deviceId')
    propsChanged(newValue: number): void {
        this.loadDeviceProps(newValue);
    }

    loadDeviceProps(deviceId: number) {
        if (this.disposed) return;
        const requestId = ++this.loadRequestId;
        this.saveRequestId++;
        this.source = null;
        this.form = {};
        this.state = State.Empty;
        this.alertMessage = '';
        this.saving = false;
        this.loading = true;
        this.loadError = false;
        return DeviceAdditionalDataRepository
            .getByDeviceId(deviceId)
            .then(response => {
                if (this.disposed || requestId !== this.loadRequestId) return;
                this.source = JSON.parse(JSON.stringify(response ?? {}));
                this.mapProps();
            })
            .catch(() => {
                if (!this.disposed && requestId === this.loadRequestId) this.loadError = true;
            })
            .finally(() => {
                if (!this.disposed && requestId === this.loadRequestId) this.loading = false;
            });
    }

    mapProps(): void {
        if (this.disposed || this.saving) return;
        this.form = {
            name: this.source?.monitoring?.name ?? '',
        };
        this.state = State.Empty;
        this.alertMessage = '';
    }

    submitForm() {
        if (this.disposed || this.loading || this.saving || !this.source) return;
        const deviceId = this.$props.deviceId;
        const requestId = ++this.saveRequestId;
        const submitted: AdditionalDataDto = JSON.parse(JSON.stringify({
            ...this.source, monitoring: { ...this.source.monitoring, name: this.form.name ?? '' },
        }));
        this.saving = true;
        this.state = State.Empty;
        this.alertMessage = '';
        return DeviceAdditionalDataRepository
            .saveDeviceAdditionalData(
                deviceId,
                submitted
            )
            .then(() => {
                this.$store.commit('clearDeviceList');
                if (this.disposed || requestId !== this.saveRequestId) return;
                this.source = submitted;
                this.state = State.Success;
                this.alertMessage = this.$t('monitoring.props.general.success').toString();
            })
            .catch(() => {
                if (this.disposed || requestId !== this.saveRequestId) return;
                this.state = State.Error;
                this.alertMessage = this.$t('additional_save_error').toString();
            })
            .finally(() => {
                if (!this.disposed && requestId === this.saveRequestId) this.saving = false;
            });
    }

    hasAnyAlert(): boolean {
        return this.state !== State.Empty;
    }

    stateAlertVariant(): string {
        switch (this.state) {
            case State.Success: return 'success';
            case State.Error: return 'danger';
            default: throw new Error('Unexpected state ' + this.state);
        }
    }
}

export default GeneralProps;
</script>

<style scoped lang="scss">
    .general-props-component {
        position: relative;

        button {
            margin-right: 0.5em;
        }
    }
</style>
